import React, { useState } from "react";
import { X, Eye, EyeOff, Code, Monitor } from "lucide-react";

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: "create" | "edit";
  template: {
    name: string;
    subject: string;
    content: string;
  };
  setTemplate: React.Dispatch<
    React.SetStateAction<{
      name: string;
      subject: string;
      content: string;
    }>
  >;
  onSave: () => void;
  isLoading: boolean;
  showPreview: boolean;
  setShowPreview: (show: boolean) => void;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  isOpen,
  onClose,
  mode = "create",
  template,
  setTemplate,
  onSave,
  isLoading,
  showPreview,
  setShowPreview,
}) => {
  const [previewMode, setPreviewMode] = useState<"preview" | "code">("preview");
  const isEditMode = mode === "edit";

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              {isEditMode ? "Edit Email Template" : "Create Email Template"}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {showPreview ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                {showPreview ? "Hide Preview" : "Show Preview"}
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Form Section */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={template.name}
                  onChange={(e) =>
                    setTemplate({ ...template, name: e.target.value })
                  }
                  placeholder="Enter template name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={template.subject}
                  onChange={(e) =>
                    setTemplate({ ...template, subject: e.target.value })
                  }
                  placeholder="Enter email subject..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content *
                </label>
                <textarea
                  value={template.content}
                  onChange={(e) =>
                    setTemplate({ ...template, content: e.target.value })
                  }
                  placeholder="Enter email content..."
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Preview Section */}
          {showPreview && (
            <div className="w-1/2 border-l border-gray-200 overflow-y-auto p-6 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-gray-700">Preview</h4>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewMode("preview")}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                      previewMode === "preview"
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <Monitor className="h-3 w-3" />
                    Preview
                  </button>
                  <button
                    onClick={() => setPreviewMode("code")}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                      previewMode === "code"
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <Code className="h-3 w-3" />
                    Code
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="border-b border-gray-200 pb-2 mb-4">
                  <p className="text-sm text-gray-600">Subject:</p>
                  <p className="font-medium text-gray-900">
                    {template.subject || "No subject"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2">Content:</p>
                  {previewMode === "preview" ? (
                    <div className="email-preview-container">
                      <style>
                        {`
                          .email-preview-container * {
                            max-width: 100% !important;
                          }
                          .email-preview-container img {
                            max-width: 100% !important;
                            height: auto !important;
                          }
                          .email-preview-container table {
                            width: 100% !important;
                            border-collapse: collapse !important;
                          }
                          .email-preview-container td, .email-preview-container th {
                            padding: 8px !important;
                          }
                        `}
                      </style>
                      <div
                        className="email-preview-container"
                        dangerouslySetInnerHTML={{
                          __html: template.content || "No content",
                        }}
                        style={{
                          fontFamily: "Arial, sans-serif",
                          lineHeight: "1.6",
                          color: "#333",
                        }}
                      />
                    </div>
                  ) : (
                    <div className="bg-gray-900 rounded p-3 overflow-x-auto">
                      <pre className="text-green-400 text-xs font-mono whitespace-pre-wrap">
                        {template.content || "No content"}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={
                isLoading ||
                !template.name.trim() ||
                !template.subject.trim() ||
                !template.content.trim()
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update Template"
                : "Create Template"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateModal;
